import React, { useEffect, useMemo, useState } from "react";
import Modal from "../Modal/Modal";
import mainApi from "../../utils/api";
import copyClipboard from "../../utils/copyClipboard";
import { getCachedScript } from "../../utils/scriptCache";
import "./TemplateMetadata.css";

const FALLBACK_PROJECT_NAME = "Template";

function TemplateMetadata({ isOpen, onClose }) {
  const [projectName, setProjectName] = useState(FALLBACK_PROJECT_NAME);
  const [description, setDescription] = useState("");
  const [tags, setTags] = useState("");
  const [loadingField, setLoadingField] = useState(null); // all | description | tags | project
  const [copiedField, setCopiedField] = useState(null);
  const [error, setError] = useState("");

  const tagsCount = useMemo(
    () =>
      tags
        .split(",")
        .map((tag) => tag.trim())
        .filter(Boolean).length,
    [tags],
  );

  useEffect(() => {
    if (!isOpen) return;

    setError("");
    setCopiedField(null);
    fetchProjectName()
      .then((name) => {
        setProjectName(name);
        requestMetadata("all", name);
      })
      .catch((err) => {
        console.error("Failed to get project name", err);
        setProjectName(FALLBACK_PROJECT_NAME);
        requestMetadata("all", FALLBACK_PROJECT_NAME);
      });
  }, [isOpen]);

  const fetchProjectName = (withLoading = true) => {
    if (withLoading) {
      setLoadingField("project");
    }

    return new Promise((resolve) => {
      try {
        getCachedScript();
        if (!window?.jsx?.evalScript) {
          resolve(FALLBACK_PROJECT_NAME);
          setLoadingField(null);
          return;
        }

        const script = `
          try {
            function __ae2GetProjectName() {
              if (app && app.project) {
                if (app.project.file) {
                  return app.project.file.displayName || app.project.file.name || app.project.file.fsName.split('/').pop();
                }
                if (app.project.name) {
                  return app.project.name;
                }
              }
              return "${FALLBACK_PROJECT_NAME}";
            }
            __ae2GetProjectName();
          } catch (err) {
            "${FALLBACK_PROJECT_NAME}";
          }
        `;

        window.jsx.evalScript(script, (result) => {
          if (withLoading) {
            setLoadingField(null);
          }
          const name = (result || "").replace(/\.[^.]+$/, "").trim();
          resolve(name || FALLBACK_PROJECT_NAME);
        });
      } catch (err) {
        if (withLoading) {
          setLoadingField(null);
        }
        resolve(FALLBACK_PROJECT_NAME);
      }
    });
  };

  const buildPrompt = (field, name) => {
    const targetName =
      (name || FALLBACK_PROJECT_NAME).trim() || FALLBACK_PROJECT_NAME;
    const needDescription = field !== "tags";
    const needTags = field !== "description";

    return `You will receive a motion graphics template name used on stock marketplaces such as Videohive, MotionArray, or Adobe Stock.
Project name: "${targetName}"
Return only JSON${needDescription && needTags ? "" : " with the requested field(s)"}.
${needDescription ? "Description: 1-2 sentences in English, highlight style, use cases, and mood." : ""}
${needTags ? "Tags: exactly 20 concise, search-friendly keywords, comma separated, no numbering or filler words." : ""}
Format strictly as:
{"description":"...","tags":["tag1","tag2",...,"tag20"]}
No code fences, markdown, or extra commentary.`;
  };

  const normalizeTags = (value) => {
    let list = [];

    if (Array.isArray(value)) {
      list = value;
    } else if (typeof value === "string") {
      list = value.split(/[,\\n]/);
    }

    const cleaned = list.map((tag) => tag.trim()).filter(Boolean);

    if (cleaned.length > 20) {
      return cleaned.slice(0, 20);
    }

    return cleaned;
  };

  const requestMetadata = async (field = "all", nameOverride) => {
    const targetField = field === "all" ? "all" : field;

    setLoadingField(targetField);
    setError("");

    try {
      const latestName =
        nameOverride ??
        (await fetchProjectName(false).then((name) => {
          setProjectName(name);
          return name;
        }));

      const prompt = buildPrompt(field, latestName);
      const answer = await mainApi.getOpenAi(prompt);
      let payload = answer?.response ?? answer;

      if (typeof payload === "string") {
        payload = JSON.parse(payload);
      }

      if (!payload || typeof payload !== "object") {
        throw new Error("Invalid response format");
      }

      if (targetField !== "tags" && payload.description) {
        setDescription(String(payload.description).trim());
      }

      if (targetField !== "description" && payload.tags) {
        const parsedTags = normalizeTags(payload.tags);
        setTags(parsedTags.join(", "));
      }
    } catch (err) {
      console.error("Failed to fetch template metadata", err);
      setError(err?.message || "Failed to fetch metadata");
    } finally {
      setLoadingField(null);
    }
  };

  const handleCopy = (field) => {
    const text = field === "description" ? description : tags;
    if (!text) return;

    copyClipboard(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 1200);
  };

  const handleClose = () => {
    setDescription("");
    setTags("");
    setError("");
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose}>
      <div className="template-metadata">
        <div className="template-metadata__header">
          <div>
            <div className="template-metadata__eyebrow">Project</div>
            <div className="template-metadata__project">{projectName}</div>
          </div>
          <div className="template-metadata__header-actions">
            <button
              className="template-metadata__button"
              onClick={() => requestMetadata("all")}
              disabled={loadingField === "all" || loadingField === "project"}
            >
              {loadingField === "all" ? "Updating..." : "Refresh both"}
            </button>
          </div>
        </div>

        {error && <div className="template-metadata__error">{error}</div>}

        <div className="template-metadata__grid">
          <div
            className="template-metadata__card"
            onClick={() => handleCopy("description")}
          >
            <div className="template-metadata__card-header">
              <div>
                <div className="template-metadata__label">Description</div>
                <div className="template-metadata__hint">
                  {copiedField === "description"
                    ? "Copied"
                    : "Click card to copy"}
                </div>
              </div>
              <button
                className="template-metadata__button template-metadata__button--ghost"
                onClick={(e) => {
                  e.stopPropagation();
                  requestMetadata("description");
                }}
                disabled={loadingField === "description"}
              >
                {loadingField === "description" ? "Updating..." : "Update"}
              </button>
            </div>
            <div className="template-metadata__value">
              {description || "No description yet"}
            </div>
          </div>

          <div
            className="template-metadata__card"
            onClick={() => handleCopy("tags")}
          >
            <div className="template-metadata__card-header">
              <div>
                <div className="template-metadata__label">Tags (20)</div>
                <div className="template-metadata__hint">
                  {copiedField === "tags" ? "Copied" : "Click card to copy"}
                </div>
              </div>
              <button
                className="template-metadata__button template-metadata__button--ghost"
                onClick={(e) => {
                  e.stopPropagation();
                  requestMetadata("tags");
                }}
                disabled={loadingField === "tags"}
              >
                {loadingField === "tags" ? "Updating..." : "Update"}
              </button>
            </div>
            <div className="template-metadata__value template-metadata__value--mono">
              {tags || "No tags yet"}
            </div>
            <div className="template-metadata__meta-row">
              <span>{tagsCount}/20 tags</span>
              <span>Comma separated</span>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}

export default TemplateMetadata;
